"use client";

import { findAnalyticsCount } from "@/services/analytics.services";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";

/**
 * VisitCounter component
 * Tracks page visits by calling the FindAnalyticsCount API.
 * Prevents duplicate counting on refresh using sessionStorage.
 */
export default function VisitCounter() {
  const pathname = usePathname();
  const [count, setCount] = useState<number | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const handleTracking = async () => {
      try {
        // User's requested key format
        const storageKey = `visited-${pathname}`;
        const lastKnownPath = sessionStorage.getItem("last_active_path");
        const cachedCount = sessionStorage.getItem(storageKey);

        /**
         * Logic to satisfy:
         * 1. On page load/change, call API.
         * 2. Refresh = NO increment (pathname stays the same in sessionStorage).
         * 3. Navigate away and back = SHOULD increment (pathname changed).
         */
        const isRefresh = lastKnownPath === pathname;

        if (isRefresh && cachedCount) {
          // It's a refresh - use the cached count from this session
          setCount(parseInt(cachedCount, 10));
          return;
        }

        // It's a new visit or return visit - call API to increment
        const result = await findAnalyticsCount(pathname);
        if (result && result.Count !== undefined) {
          const newCount = result.Count;
          setCount(newCount);

          // Store both the count and the active path to detect refreshes
          sessionStorage.setItem(storageKey, newCount.toString());
          sessionStorage.setItem("last_active_path", pathname);
        } else if (cachedCount) {
          // Fallback to cached count if API fails
          setCount(parseInt(cachedCount, 10));
        }
      } catch (error) {
        console.error("VisitCounter error:", error);
      }
    };

    handleTracking();

    // Safety for StrictMode in development (doesn't affect production behavior)
    isFirstRender.current = false;
  }, [pathname]);

  if (count === null) return null;

  return (
    <div
      id="visit-counter"
      className="inline-flex items-center gap-2 bg-pink-600 backdrop-blur-sm border border-pink-600 px-4 py-2 rounded-full shadow-sm transition-all hover:shadow-md"
    >
      <div className="flex -space-x-1">
        <div className="w-2 h-2 rounded-full bg-green-600 animate-pulse ring-4 ring-green-600/10"></div>
      </div>
      <span className="text-white font-bold text-sm tracking-tight whitespace-nowrap">
        Live Visits :{" "}
        <span className="text-white tabular-nums">
          {count.toLocaleString()}
        </span>
      </span>
    </div>
  );
}
